import React from 'react';
import { Pen, Eraser, Type, Undo2, Redo2, Trash2, Grid3X3, ArrowLeft, FilePlus2, CloudOff, Check, Loader2, History, Sparkles } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { WB_FONTS } from './whiteboardFonts';

export const WB_COLORS = [
  { id: 'black', hex: '#0f172a', label: 'Black' },
  { id: 'blue', hex: '#2563eb', label: 'Blue' },
  { id: 'red', hex: '#dc2626', label: 'Red' },
  { id: 'green', hex: '#16a34a', label: 'Green' },
  { id: 'orange', hex: '#ea580c', label: 'Orange' },
  { id: 'purple', hex: '#7c3aed', label: 'Purple' },
];

export const WB_WIDTHS = [
  { id: 'thin', px: 2, label: 'Thin' },
  { id: 'medium', px: 5, label: 'Medium' },
  { id: 'thick', px: 10, label: 'Thick' },
];

// WB-TEXT-EDIT-001: dedicated text sizes (shown in place of pen thickness while the Text tool is active)
export const WB_TEXT_SIZES = [
  { id: 's', px: 18, label: 'Small text', glyph: 11 },
  { id: 'm', px: 24, label: 'Medium text', glyph: 14 },
  { id: 'l', px: 32, label: 'Large text', glyph: 17 },
  { id: 'xl', px: 44, label: 'Extra large text', glyph: 21 },
];

export const WB_ERASER_SIZES = [
  { id: 'small', px: 20, label: 'Small eraser' },
  { id: 'medium', px: 40, label: 'Medium eraser' },
  { id: 'large', px: 70, label: 'Large eraser' },
];

const SAVE_LABELS = {
  idle: '',
  pending: 'Saving…',
  saved: 'Saved locally',
  restored: 'Restored previous session',
  empty: 'Nothing to save',
  too_large: 'Board too large to save locally',
  error: 'Local save unavailable',
};

const ToolButton = ({ testId, active, disabled, onClick, label, shortcut, children, danger }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        data-testid={testId}
        aria-label={label}
        aria-pressed={active}
        disabled={disabled}
        onClick={onClick}
        className={`h-9 w-9 inline-flex items-center justify-center rounded-md transition-colors duration-150 disabled:opacity-35 disabled:cursor-not-allowed ${
          active
            ? 'bg-blue-600 text-white shadow-sm'
            : danger
              ? 'text-slate-500 hover:bg-red-50 hover:text-red-600'
              : 'text-slate-600 hover:bg-slate-200/70 hover:text-slate-900'
        }`}
      >
        {children}
      </button>
    </TooltipTrigger>
    <TooltipContent side="bottom" className="text-xs">
      {label}{shortcut ? <span className="ml-1.5 text-slate-400">{shortcut}</span> : null}
    </TooltipContent>
  </Tooltip>
);

const Divider = () => <span className="h-6 w-px bg-slate-200 mx-1" aria-hidden="true" />;

// WB-TEXT-FMT-001: compact font style + size group (Text tool, and Pen while Ink-to-Text is on)
const TextFormatGroup = ({ textFont, setTextFont, textSize, setTextSize }) => (
  <div className="flex items-center gap-1" data-testid="wb-text-format-group">
    <div className="flex items-center rounded-md bg-slate-200/60 p-0.5" role="radiogroup" aria-label="Font style" data-testid="wb-font-group">
      {WB_FONTS.map((f) => (
        <Tooltip key={f.id}>
          <TooltipTrigger asChild>
            <button
              type="button"
              role="radio"
              aria-checked={textFont === f.id}
              aria-label={`${f.label} font`}
              data-testid={`wb-font-${f.id}`}
              onClick={() => setTextFont(f.id)}
              className={`h-8 min-w-8 px-1.5 inline-flex items-center justify-center rounded transition-colors leading-none ${
                textFont === f.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
              }`}
              style={{ fontFamily: f.family, fontWeight: f.weight, fontSize: Math.round(15 * f.scale) }}
            >
              Aa
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">{f.label}</TooltipContent>
        </Tooltip>
      ))}
    </div>
    <div className="flex items-center rounded-md bg-slate-200/60 p-0.5" role="radiogroup" aria-label="Text size" data-testid="wb-text-size-group">
      {WB_TEXT_SIZES.map((w) => (
        <button
          key={w.id}
          type="button"
          role="radio"
          aria-checked={textSize === w.px}
          aria-label={w.label}
          title={`${w.label} (${w.px}px)`}
          data-testid={`wb-text-size-${w.id}`}
          onClick={() => setTextSize(w.px)}
          className={`h-8 w-8 inline-flex items-center justify-center rounded text-[11px] font-semibold uppercase transition-colors ${
            textSize === w.px ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          {w.id}
        </button>
      ))}
    </div>
  </div>
);

export const WhiteboardToolbar = ({
  tool, setTool, color, setColor, width, setWidth, eraserSize, setEraserSize, textSize, setTextSize, textFont, setTextFont, showGrid, setShowGrid,
  canUndo, canRedo, onUndo, onRedo, onClear, onStartNew, hasContent, saveStatus, onBack, backLabel,
  inkEnabled, onToggleInk, inkAutoConvert, onToggleInkAuto, inkBusy,
}) => (
  <TooltipProvider delayDuration={200}>
    <header
      data-testid="whiteboard-toolbar"
      className="h-14 shrink-0 flex items-center gap-1 px-3 bg-slate-50 border-b border-slate-200"
    >
      <button
        type="button"
        data-testid="whiteboard-back-btn"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-200/70 hover:text-slate-900 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        {backLabel}
      </button>
      <Divider />
      <span className="hidden md:inline text-xs font-semibold uppercase tracking-[0.05em] text-slate-500 mr-1" data-testid="whiteboard-title">
        Training Whiteboard
      </span>
      <Divider />

      <ToolButton testId="wb-tool-pen" label="Pen" shortcut="P" active={tool === 'pen'} onClick={() => setTool('pen')}>
        <Pen className="h-4 w-4" />
      </ToolButton>
      <ToolButton testId="wb-tool-eraser" label="Eraser" shortcut="E" active={tool === 'eraser'} onClick={() => setTool('eraser')}>
        <Eraser className="h-4 w-4" />
      </ToolButton>
      <ToolButton testId="wb-tool-text" label="Text" shortcut="T" active={tool === 'text'} onClick={() => setTool('text')}>
        <Type className="h-4 w-4" />
      </ToolButton>
      <Divider />

      <div className="flex items-center gap-1" role="radiogroup" aria-label="Color" data-testid="wb-color-group">
        {WB_COLORS.map((c) => (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={color === c.hex}
            aria-label={c.label}
            data-testid={`wb-color-${c.id}`}
            onClick={() => setColor(c.hex)}
            className={`h-6 w-6 rounded-full border-2 transition-transform duration-150 hover:scale-110 ${
              color === c.hex ? 'border-slate-900 scale-110 ring-2 ring-white' : 'border-transparent'
            }`}
            style={{ backgroundColor: c.hex }}
          />
        ))}
      </div>
      <Divider />

      {tool === 'text' ? (
        <TextFormatGroup textFont={textFont} setTextFont={setTextFont} textSize={textSize} setTextSize={setTextSize} />
      ) : (
      <div className="flex items-center gap-1" role="radiogroup" aria-label="Thickness" data-testid="wb-width-group">
        {tool === 'eraser'
          ? WB_ERASER_SIZES.map((w) => (
            <button
              key={w.id}
              type="button"
              role="radio"
              aria-checked={eraserSize === w.px}
              aria-label={w.label}
              data-testid={`wb-eraser-${w.id}`}
              onClick={() => setEraserSize(w.px)}
              className={`h-9 w-9 inline-flex items-center justify-center rounded-md transition-colors ${
                eraserSize === w.px ? 'bg-slate-200 text-slate-900' : 'text-slate-500 hover:bg-slate-200/70'
              }`}
            >
              <span className="rounded-full border-2 border-current" style={{ width: 8 + w.px / 4, height: 8 + w.px / 4 }} />
            </button>
          ))
          : WB_WIDTHS.map((w) => (
            <button
              key={w.id}
              type="button"
              role="radio"
              aria-checked={width === w.px}
              aria-label={w.label}
              data-testid={`wb-width-${w.id}`}
              onClick={() => setWidth(w.px)}
              className={`h-9 w-9 inline-flex items-center justify-center rounded-md transition-colors ${
                width === w.px ? 'bg-slate-200 text-slate-900' : 'text-slate-500 hover:bg-slate-200/70'
              }`}
            >
              <span className="rounded-full bg-current" style={{ width: w.px + 2, height: w.px + 2 }} />
            </button>
          ))}
      </div>
      )}
      <Divider />

      <ToolButton testId="wb-undo" label="Undo" shortcut="Ctrl+Z" disabled={!canUndo} onClick={onUndo}>
        <Undo2 className="h-4 w-4" />
      </ToolButton>
      <ToolButton testId="wb-redo" label="Redo" shortcut="Ctrl+Y" disabled={!canRedo} onClick={onRedo}>
        <Redo2 className="h-4 w-4" />
      </ToolButton>
      <ToolButton testId="wb-clear" label="Clear all" disabled={!hasContent} onClick={onClear} danger>
        <Trash2 className="h-4 w-4" />
      </ToolButton>
      <ToolButton testId="wb-start-new" label="Start new board (removes saved draft)" onClick={onStartNew} danger>
        <FilePlus2 className="h-4 w-4" />
      </ToolButton>
      <Divider />

      <ToolButton testId="wb-grid-toggle" label={showGrid ? 'Hide dot grid' : 'Show dot grid'} shortcut="G" active={showGrid} onClick={() => setShowGrid((v) => !v)}>
        <Grid3X3 className="h-4 w-4" />
      </ToolButton>
      {onToggleInk && (
        <ToolButton
          testId="wb-ink-toggle"
          label={inkEnabled ? 'Ink-to-Text (AI) is on — click to turn off' : 'Ink-to-Text (AI): convert handwriting to typed text'}
          active={inkEnabled}
          onClick={onToggleInk}
        >
          <Sparkles className={`h-4 w-4 ${inkBusy ? 'wb-ink-busy' : ''}`} data-testid="wb-ink-icon" data-busy={inkBusy ? 'true' : 'false'} />
        </ToolButton>
      )}
      {onToggleInk && inkEnabled && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              data-testid="wb-ink-auto-toggle"
              role="switch"
              aria-checked={inkAutoConvert}
              aria-label="Auto Convert handwriting"
              onClick={onToggleInkAuto}
              className={`h-7 px-2 inline-flex items-center gap-1.5 rounded-full text-[11px] font-semibold tracking-wide transition-colors duration-150 ${
                inkAutoConvert ? 'bg-emerald-600 text-white shadow-sm' : 'bg-slate-200/70 text-slate-600 hover:bg-slate-300/70'
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${inkAutoConvert ? 'bg-white' : 'bg-slate-400'}`} aria-hidden="true" />
              AUTO
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs max-w-[260px]">
            {inkAutoConvert
              ? 'Auto Convert on: clearly recognized handwriting becomes text automatically (Undo restores the ink). Unclear results still ask first.'
              : 'Auto Convert off: every recognition asks “Convert to …?” first.'}
          </TooltipContent>
        </Tooltip>
      )}
      {onToggleInk && inkEnabled && tool === 'pen' && (
        <>
          <Divider />
          <TextFormatGroup textFont={textFont} setTextFont={setTextFont} textSize={textSize} setTextSize={setTextSize} />
        </>
      )}

      <span
        className={`ml-auto inline-flex items-center gap-1.5 text-xs ${
          saveStatus === 'too_large' || saveStatus === 'error' ? 'text-amber-600' : 'text-slate-400'
        }`}
        data-testid="wb-save-status"
        data-status={saveStatus}
      >
        {saveStatus === 'pending' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {saveStatus === 'saved' && <Check className="h-3.5 w-3.5 text-emerald-500" />}
        {saveStatus === 'restored' && <History className="h-3.5 w-3.5 text-blue-500" />}
        {(saveStatus === 'too_large' || saveStatus === 'error') && <CloudOff className="h-3.5 w-3.5" />}
        {SAVE_LABELS[saveStatus] || ''}
      </span>
    </header>
  </TooltipProvider>
);
